import { FC, ReactNode, useState } from 'react';

import { Dialog, DialogActions, DialogContent, DialogTitle, Tab, Tabs } from '@mui/material';

import { CreatedMcpApiKey } from '@/apis/admin-api';
import { CodeBlock } from '@/components/CodeBlock';
import { CopyToken } from '@/components/CopyToken';
import { PrimaryButton } from '@/components/Shared/Buttons';

import { getKeyServerUrl } from '../constants';

import styles from '../styles.module.scss';

type CreatedKeyDialogProps = {
  createdKey: CreatedMcpApiKey;
  isRotated?: boolean;
  onClose: () => void;
};

type Snippet = { label: string; language: string; code: string; note?: ReactNode };

const getSnippets = (url: string, key: string): Snippet[] => [
  {
    label: 'Claude Code',
    language: 'bash',
    note: (
      <>
        First run <code>read -rs TREBLE_MCP_KEY</code> and paste the key, so it isn't saved in your shell history.
      </>
    ),
    code: `claude mcp add --transport http treble ${url} \\\n  --header "Authorization: Bearer $TREBLE_MCP_KEY"`,
  },
  {
    label: 'Cursor (mcp.json)',
    language: 'json',
    code: JSON.stringify({ mcpServers: { treble: { url, headers: { Authorization: `Bearer ${key}` } } } }, null, 2),
  },
  {
    label: 'Other MCP clients',
    language: 'text',
    code: `URL: ${url}\nHeader: Authorization: Bearer ${key}`,
  },
];

export const CreatedKeyDialog: FC<CreatedKeyDialogProps> = ({ createdKey, isRotated = false, onClose }) => {
  const [tab, setTab] = useState(0);
  const snippets = getSnippets(getKeyServerUrl(createdKey), createdKey.key);

  return (
    <Dialog open maxWidth="md" fullWidth className={styles['created-dialog']}>
      <DialogTitle>{isRotated ? 'MCP API key rotated' : 'MCP API key created'}</DialogTitle>
      {/* Keep the secret out of Datadog session replays and action names. */}
      <DialogContent data-dd-privacy="mask" data-dd-action-name="MCP API key setup">
        <p className={styles['warning']}>
          Copy the key now. You won't be able to see it again. If you lose it, rotate it to get a new one.
          {isRotated && ' The old key stops working within 5 minutes: update every client that used it.'}
        </p>
        <CopyToken token={createdKey.key} />
        <p className={styles['setup-title']}>Set up your MCP client</p>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} className={styles['tabs']}>
          {snippets.map(({ label }) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
        {snippets[tab].note && <p>{snippets[tab].note}</p>}
        <CodeBlock showLineNumbers={false} language={snippets[tab].language}>
          {snippets[tab].code}
        </CodeBlock>
      </DialogContent>
      <DialogActions>
        <PrimaryButton label="Done" onClick={onClose} width={150} />
      </DialogActions>
    </Dialog>
  );
};
